// frontend/src/components/BranchManagement.js (New)
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  IconButton,
  Alert,
  Switch,
  FormControlLabel,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  Palette as PaletteIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BranchManagement = () => {
  const { isSuperAdmin } = useAuth();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    displayName: '',
    status: 'Active',
    settings: {
      theme: {
        primaryColor: '#1976d2',
        logo: '',
        favicon: ''
      },
      contact: {
        email: '',
        phone: '',
        address: ''
      },
      features: {
        allowPublicSubmissions: true,
        requireEventCode: false,
        customFields: []
      }
    }
  });

  useEffect(() => {
    if (isSuperAdmin()) {
      fetchBranches();
    }
  }, [isSuperAdmin]);

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/branches', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(response.data);
    } catch (error) {
      showSnackbar('Failed to fetch branches', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddBranch = () => {
    setEditingBranch(null);
    setFormData({
      name: '',
      code: '',
      displayName: '',
      status: 'Active',
      settings: {
        theme: {
          primaryColor: '#1976d2',
          logo: '',
          favicon: ''
        },
        contact: {
          email: '',
          phone: '',
          address: ''
        },
        features: {
          allowPublicSubmissions: true,
          requireEventCode: false,
          customFields: []
        }
      }
    });
    setTabValue(0);
    setOpenDialog(true);
  };

  const handleEditBranch = (branch) => {
    setEditingBranch(branch);
    setFormData({
      name: branch.name || '',
      code: branch.code || '',
      displayName: branch.displayName || '',
      status: branch.status || 'Active',
      settings: {
        theme: {
          primaryColor: branch.settings?.theme?.primaryColor || '#1976d2',
          logo: branch.settings?.theme?.logo || '',
          favicon: branch.settings?.theme?.favicon || ''
        },
        contact: {
          email: branch.settings?.contact?.email || '',
          phone: branch.settings?.contact?.phone || '',
          address: branch.settings?.contact?.address || ''
        },
        features: {
          allowPublicSubmissions: branch.settings?.features?.allowPublicSubmissions ?? true,
          requireEventCode: branch.settings?.features?.requireEventCode ?? false,
          customFields: branch.settings?.features?.customFields || []
        }
      }
    });
    setTabValue(0);
    setOpenDialog(true);
  };

  const handleSaveBranch = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (editingBranch) {
        await axios.put(`/api/branches/${editingBranch._id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showSnackbar('Branch updated successfully');
      } else {
        await axios.post('/api/branches', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showSnackbar('Branch created successfully');
      }
      
      fetchBranches();
      setOpenDialog(false);
    } catch (error) {
      showSnackbar(
        error.response?.data?.error || 'Failed to save branch',
        'error'
      );
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [section]: {
          ...prev.settings[section],
          [field]: value
        }
      }
    }));
  };

  if (!isSuperAdmin()) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. Only Super Administrators can manage branches.
        </Alert>
      </Box>
    );
  }

  const handleDeleteBranch = async (branchId) => {
  if (!window.confirm('Are you sure you want to delete this branch?')) return;

  try {
    const token = localStorage.getItem('token');
    await axios.delete(`/api/branches/${branchId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    showSnackbar('Branch deleted successfully', 'success');
    fetchBranches(); // refresh the list
  } catch (error) {
    console.error(error);
    showSnackbar('Failed to delete branch', 'error');
  }
};

  const BasicInfoTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          label="Branch Name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          fullWidth
          required
          helperText="Internal name for the branch"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Branch Code"
          value={formData.code}
          onChange={(e) => handleInputChange('code', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
          fullWidth
          required
          helperText="Unique identifier (lowercase, no spaces)"
          disabled={editingBranch} // Don't allow editing code for existing branches
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Display Name"
          value={formData.displayName}
          onChange={(e) => handleInputChange('displayName', e.target.value)}
          fullWidth
          required
          helperText="Name shown to users"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Contact Email"
          type="email"
          value={formData.settings.contact.email}
          onChange={(e) => handleNestedInputChange('contact', 'email', e.target.value)}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Contact Phone"
          value={formData.settings.contact.phone}
          onChange={(e) => handleNestedInputChange('contact', 'phone', e.target.value)}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Address"
          value={formData.settings.contact.address}
          onChange={(e) => handleNestedInputChange('contact', 'address', e.target.value)}
          fullWidth
          multiline
          rows={2}
        />
      </Grid>
    </Grid>
  );

  const SettingsTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Features
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.settings.features.allowPublicSubmissions}
              onChange={(e) => handleNestedInputChange('features', 'allowPublicSubmissions', e.target.checked)}
            />
          }
          label="Allow Public Submissions"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.settings.features.requireEventCode}
              onChange={(e) => handleNestedInputChange('features', 'requireEventCode', e.target.checked)}
            />
          }
          label="Require Event Code"
        />
      </Grid>
      
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Appearance
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Primary Color"
          type="color"
          value={formData.settings.theme.primaryColor}
          onChange={(e) => handleNestedInputChange('theme', 'primaryColor', e.target.value)}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Logo URL"
          value={formData.settings.theme.logo}
          onChange={(e) => handleNestedInputChange('theme', 'logo', e.target.value)}
          fullWidth
          helperText="URL to branch logo image"
        />
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Branch Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage all division branches of the Broker Union platform
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddBranch}
          sx={{ borderRadius: 2 }}
        >
          Add Branch
        </Button>
      </Box>

      {/* Branches Table */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Branch</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Users</TableCell>
                  <TableCell align="center">Submissions</TableCell>
                  <TableCell align="center">Events</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {branches.map((branch) => {
                  const stats = {
                   users: branch.stats.users || 0,
                   submissions: branch.stats.submissionsCount || 0,
                   events: branch.stats.eventsCount || 0
                  };
                  return (
                    <TableRow key={branch._id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <BusinessIcon color="primary" />
                          <Box>
                            <Typography variant="subtitle2" fontWeight="medium">
                              {branch.displayName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {branch.name}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={branch.code} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={branch.status}
                          color={branch.status === 'Active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">{branch.stats.users}</TableCell>
                      <TableCell align="center">{branch.stats.submissions}</TableCell>
                      <TableCell align="center">{branch.stats.events}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleEditBranch(branch)}
                          color="primary"
                        >
                          <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteBranch(branch._id)}
                            color="error"
                          >
                            <DeleteIcon />  
                            </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Branch Form Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingBranch ? 'Edit Branch' : 'Create New Branch'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab label="Basic Info" icon={<BusinessIcon />} />
              <Tab label="Settings" icon={<SettingsIcon />} />
            </Tabs>
          </Box>

          <Box sx={{ pt: 2 }}>
            {tabValue === 0 && <BasicInfoTab />}
            {tabValue === 1 && <SettingsTab />}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveBranch} 
            variant="contained"
            disabled={!formData.name || !formData.code || !formData.displayName}
          >
            {editingBranch ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      {snackbar.open && (
        <Alert 
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999 }}
        >
          {snackbar.message}
        </Alert>
      )}
    </Box>
  );
};

export default BranchManagement;